<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UploadInvoiceLines',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('UploadInvoiceLines');
  const resultText = ref(null);
  const downloadTemplateURL = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_3086171180629160_validation = computed(() => [
    'ext:xlsx',
    'required',
  ]);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    resultText: resultText.value,
    downloadTemplateURL: downloadTemplateURL.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3086171180629160_validation: b_3086171180629160_validation.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_downloadTemplateURL = async () => {
    return $fn.value.urlFor('download_file_template_invoice_lines_upload');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'resultText' },
      { name: 'downloadTemplateURL' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3086171180629160_validation', nodeId: '3086171180629160' },
    ],
    loaders: {
      formId: $$load_formId,
      downloadTemplateURL: $$load_downloadTemplateURL,
    },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      resultText,
      downloadTemplateURL,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3086171180629160_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'UploadInvoiceLines|Title',
        'Upload invoice lines'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_xeri18c17_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_648747096704084_register_vvov1mor0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_648747096704084_setData_fqa2xvzib_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.ngettext(
        'One line has been imported.',
        '${n} lines have been imported.',
        resultLength
      ),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_648747096704084_success_h8n36hewq_getWhen = async ($event) => {
    return $event.data.resultLength > 0;
  };
  const act_648747096704084_setData_4172ttbzm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.gettext('No lines have been imported.'),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_648747096704084_success_i0dk2f00z_getWhen = async ($event) => {
    return !$event.data.resultLength;
  };
  const act_648747096704084_request_jivy5nht0_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'upload_invoice_lines',
      data: { file: file.value },
      parameters: {
        invoice_id: props.invoice.id,
      },
      contentType: 'multipart/form-data',
    };
  };
  const act_2434514588044777_closeModal_eacwvl4ho_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_xeri18c17 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_xeri18c17_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ycym5uqed =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_xeri18c17,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ycym5uqed],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kutvrtmr4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5sllydxt5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kutvrtmr4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5sllydxt5],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ijf8ify06 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_d00hjh0vb =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ijf8ify06,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_d00hjh0vb],
    event: 'close',
  });
  const act_648747096704084_register_vvov1mor0 = new actionTypes.RegisterAction(
    {
      actionArgs: act_648747096704084_register_vvov1mor0_getActionArgs,
    }
  );
  const inst_evh_648747096704084_success_9e2kn70v4 =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_register_vvov1mor0,
      event: 'success',
      displayName: 'register',
    });
  const act_648747096704084_setData_fqa2xvzib = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_fqa2xvzib_getActionArgs,
  });
  const inst_evh_648747096704084_success_h8n36hewq =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_fqa2xvzib,
      event: 'success',
      when: inst_evh_648747096704084_success_h8n36hewq_getWhen,
      displayName: 'setData',
    });
  const act_648747096704084_setData_4172ttbzm = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_4172ttbzm_getActionArgs,
  });
  const inst_evh_648747096704084_success_i0dk2f00z =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_4172ttbzm,
      event: 'success',
      when: inst_evh_648747096704084_success_i0dk2f00z_getWhen,
      displayName: 'setData',
    });
  const act_648747096704084_request_jivy5nht0 = new actionTypes.RequestAction({
    actionArgs: act_648747096704084_request_jivy5nht0_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_648747096704084_success_9e2kn70v4,
      inst_evh_648747096704084_success_h8n36hewq,
      inst_evh_648747096704084_success_i0dk2f00z,
    ],
  });
  const inst_evh_648747096704084_submit_nqtzts7dj =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_request_jivy5nht0,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_648747096704084_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_submit_nqtzts7dj],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_eacwvl4ho =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_eacwvl4ho_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_efts8pbu6 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_eacwvl4ho,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_efts8pbu6],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ycym5uqed,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_5sllydxt5,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_d00hjh0vb,
    inst_evh_8564662037462133_close,
    inst_evh_648747096704084_success_9e2kn70v4,
    inst_evh_648747096704084_success_h8n36hewq,
    inst_evh_648747096704084_success_i0dk2f00z,
    inst_evh_648747096704084_submit_nqtzts7dj,
    submit: inst_evh_648747096704084_submit_nqtzts7dj,
    inst_evh_648747096704084_submit,
    inst_evh_2434514588044777_clickPreventStop_efts8pbu6,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ycym5uqed = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ycym5uqed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_5sllydxt5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5sllydxt5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_d00hjh0vb = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_d00hjh0vb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_9e2kn70v4 = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_9e2kn70v4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_h8n36hewq = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_h8n36hewq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_i0dk2f00z = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_i0dk2f00z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit_nqtzts7dj = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit_nqtzts7dj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_efts8pbu6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_efts8pbu6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="UploadInvoiceLines"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p>
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText wrap>
            Számlasorok a következő sablon kitöltésével tölthetők fel:
          </ItpText>
          <ItpLink
            :text="pgettext('UploadInvoiceLines', 'Download template')"
            :href="downloadTemplateURL"
            button
            variant="light"
            use-auth
            force-download
          >
          </ItpLink>
        </p>
      </ItpAlert>
      <hr />
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_648747096704084_submit($event, {})"
        >
          <ItpFormGroup
            name="file"
            :label="pgettext('UploadInvoiceLines', 'Invoice lines file')"
            labelFor="file"
            :description="
              pgettext(
                'UploadInvoiceLines',
                'Please select a filled-in template file.'
              )
            "
            required
          >
            <ItpFormFile
              v-model="file"
              :value="file"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (file =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              name="file"
              accept=".xlsx"
              required
              :validation="b_3086171180629160_validation"
            >
            </ItpFormFile>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert :show="resultText && !actions.submit.isRunning">
          <p>
            {{ resultText }}
          </p>
        </ItpAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('UploadInvoiceLines', 'Upload lines')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
